// Mockup tokens mapped onto the app's theme(mode) scale (see theme.js — the
// light palette was itself derived from this same portal redesign, so the
// mapping is close everywhere). A couple of mockup colors have no clean
// theme equivalent (a muted glyph, the drawer's outer border); those keep a
// literal hex constant, matching the precedent already set by StatusChip.jsx
// and indexStyles.showAllEnvsBox in the sibling Environments/styles.js.
export const MONO_FONT = "ui-monospace, Menlo, Consolas, monospace";

export const drawerStyles = {
  paper: (colors, width = 580) => ({
    width: { xs: "100%", md: width },
    maxWidth: "100vw",
    height: "100vh",
    display: "flex",
    flexDirection: "column",
    backgroundColor: colors.primary[700],
    borderLeft: { xs: "none", md: `1px solid ${colors.ctl}` },
    boxShadow: "-8px 0 32px rgba(16,24,40,.18)",
  }),
  header: (colors) => ({
    padding: "16px 20px 14px",
    backgroundColor: colors.primary[600],
    borderBottom: `1px solid ${colors.grey[800]}`,
    flexShrink: 0,
  }),
  headerTopRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "12px",
  },
  headerTitleRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexWrap: "wrap",
  },
  headerName: (colors) => ({
    fontSize: "16px",
    fontWeight: 600,
    letterSpacing: "-0.01em",
    color: colors.grey[100],
  }),
  headerDescription: (colors) => ({
    fontSize: "13px",
    color: colors.grey[500],
    mt: "2px",
  }),
  headerMeta: (colors) => ({
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "6px",
    fontSize: "12.5px",
    color: colors.grey[500],
    mt: "8px",
  }),
  headerMetaSeparator: (colors) => ({
    color: colors.sepMuted,
  }),
  headerMetaLink: (colors) => ({
    fontFamily: MONO_FONT,
    color: colors.blue[500],
    textDecoration: "none",
    "&:hover": { textDecoration: "underline" },
  }),
  headerMetaMono: {
    fontFamily: MONO_FONT,
  },
  body: (colors) => ({
    flex: 1,
    minHeight: 0,
    overflow: "auto",
    backgroundColor: colors.drawerBody,
    padding: "16px 20px 20px",
    display: "flex",
    flexDirection: "column",
    gap: "14px",
  }),
  unavailableBanner: (colors) => ({
    p: "16px",
    borderRadius: "8px",
    border: `1px solid ${colors.red[100]}`,
    backgroundColor: colors.red[900],
    color: colors.red[500],
    fontSize: "13px",
  }),
  footer: (colors) => ({
    padding: "12px 20px",
    borderTop: `1px solid ${colors.grey[800]}`,
    backgroundColor: colors.primary[700],
    flexShrink: 0,
    fontSize: "12px",
    color: colors.grey[400],
  }),
};

export const sectionCardStyles = {
  // flex: none is load-bearing — without it these cards shrink inside the
  // scrolling body and clip their own rows instead of the body scrolling.
  root: (colors) => ({
    flex: "none",
    border: `1px solid ${colors.grey[800]}`,
    borderRadius: "8px",
    backgroundColor: colors.primary[700],
    overflow: "hidden",
  }),
  head: (colors) => ({
    padding: "11px 14px",
    borderBottom: `1px solid ${colors.primary[400]}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
  }),
  headTitle: (colors) => ({
    fontSize: "11.5px",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: colors.grey[500],
  }),
  headAction: (colors) => ({
    height: "26px",
    padding: "0 10px",
    borderRadius: "6px",
    border: `1px solid ${colors.ctl}`,
    backgroundColor: "transparent",
    color: colors.blue[500],
    fontSize: "10.5px",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.02em",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    "&:hover": {
      borderColor: colors.blue[500],
      backgroundColor: colors.blue[100],
    },
    "&:disabled": { color: colors.grey[400], cursor: "not-allowed" },
  }),
  body: {
    padding: "0 14px",
  },
};

export const detailRowStyles = {
  row: (colors) => ({
    display: "flex",
    alignItems: "flex-start",
    gap: "12px",
    padding: "8px 0",
    borderBottom: `1px solid ${colors.primary[400]}`,
    "&:last-of-type": { borderBottom: "none" },
    "@media (max-width: 580px)": {
      flexDirection: "column",
      gap: "2px",
    },
  }),
  label: (colors) => ({
    width: "150px",
    flexShrink: 0,
    fontSize: "12.5px",
    color: colors.grey[500],
    paddingTop: "1px",
  }),
  valueGroup: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  value: (colors) => ({
    fontSize: "12.5px",
    color: colors.grey[100],
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
  }),
  valueMono: {
    fontFamily: MONO_FONT,
    fontWeight: 500,
  },
  valueMuted: (colors) => ({
    color: colors.grey[400],
  }),
  subLine: (colors) => ({
    fontSize: "11.5px",
    color: colors.grey[500],
    mt: "2px",
  }),
  maskedDots: {
    letterSpacing: "0.14em",
  },
  iconButton: (colors) => ({
    width: "22px",
    height: "22px",
    flexShrink: 0,
    color: colors.railHeading,
    "&:hover": { color: colors.ctlInk },
  }),
  icon: { fontSize: "13px" },
};

export const chipRowStyles = {
  chip: (colors) => ({
    height: "20px",
    borderRadius: "4px",
    fontSize: "10.5px",
    fontWeight: 600,
    backgroundColor: colors.primary[400],
    color: colors.grey[700],
  }),
};

export const membersStyles = {
  toggle: (colors) => ({
    fontSize: "10.5px",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.02em",
    color: colors.blue[500],
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "2px",
    background: "none",
    border: "none",
    padding: 0,
    "&:hover": { textDecoration: "underline" },
  }),
  expanded: (colors) => ({
    mt: "10px",
    pt: "10px",
    borderTop: `1px solid ${colors.primary[400]}`,
  }),
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    mb: "10px",
  },
  searchField: (colors) => ({
    flex: 1,
    "& .MuiOutlinedInput-root": {
      height: "30px",
      fontSize: "12px",
      borderRadius: "6px",
    },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: colors.ctl },
  }),
  copyAll: (colors) => ({
    fontSize: "10.5px",
    fontWeight: 600,
    textTransform: "uppercase",
    color: colors.blue[500],
    cursor: "pointer",
    background: "none",
    border: "none",
    whiteSpace: "nowrap",
    "&:hover": { textDecoration: "underline" },
  }),
  summaryLine: (colors) => ({
    fontSize: "11.5px",
    color: colors.grey[500],
    mb: "6px",
  }),
  groupHeader: (colors) => ({
    display: "flex",
    alignItems: "baseline",
    gap: "6px",
    mt: "12px",
    padding: "16px 0 8px",
    borderTop: `1px solid ${colors.primary[400]}`,
    "&:first-of-type": { mt: 0, borderTop: "none", paddingTop: 0 },
  }),
  groupHeaderTitle: (colors) => ({
    fontSize: "11px",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: colors.grey[600],
  }),
  groupHeaderCount: (colors) => ({
    fontSize: "11.5px",
    color: colors.grey[400],
  }),
  list: {
    maxHeight: "260px",
    overflow: "auto",
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "8px 24px",
    "@media (max-width: 580px)": {
      gridTemplateColumns: "1fr",
    },
    marginBottom: "12px",
  },
  memberItem: (colors) => ({
    fontSize: "13px",
    lineHeight: 1.4,
    color: colors.grey[700],
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    "&:hover .member-copy": { opacity: 1 },
  }),
  // Deliberately loud — the point of this badge is to be spotted at a
  // glance in a dense list, not to blend in with the surrounding text.
  memberYou: (colors) => ({
    flexShrink: 0,
    fontSize: "10px",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.03em",
    color: colors.blue[600],
    backgroundColor: colors.blue[100],
    padding: "2px 7px",
    borderRadius: "999px",
  }),
  memberCopyIcon: (colors) => ({
    opacity: 0,
    width: "16px",
    height: "16px",
    flexShrink: 0,
    color: colors.grey[400],
  }),
  emptyState: (colors) => ({
    fontSize: "12px",
    color: colors.grey[400],
    padding: "12px 0",
  }),
};

export const identifiersStyles = {
  summaryRow: (colors) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 0",
    cursor: "pointer",
  }),
  summaryText: (colors) => ({
    fontSize: "12.5px",
    color: colors.grey[500],
  }),
  chevron: (colors) => ({
    width: "20px",
    height: "20px",
    color: colors.railHeading,
    transition: "transform 120ms ease",
  }),
  chevronOpen: {
    transform: "rotate(180deg)",
  },
};

// Chevron column in the table and the "open" tint on the row it belongs to.
export const rowOpenStyles = {
  chevronButton: (colors, open) => ({
    width: "28px",
    height: "28px",
    borderRadius: "5px",
    color: open ? colors.btnHover : colors.railHeading,
    backgroundColor: open ? colors.openTint : "transparent",
    "&:hover": {
      backgroundColor: open ? colors.openTint : colors.ctlHoverBg,
      color: open ? colors.btnHover : colors.ctlInk,
    },
  }),
  chevronIcon: (open) => ({
    fontSize: "17px",
    transform: open ? "rotate(90deg)" : "none",
    transition: "transform 120ms ease",
  }),
  // Consumed as a getRowClassName + sx pair on the DataTable; both the plain
  // and :hover forms are set so this beats the grid's default row hover.
  gridRowOpenRule: (colors) => ({
    "& .MuiDataGrid-row.env-row-open, & .MuiDataGrid-row.env-row-open:hover": {
      backgroundColor: colors.openRow,
      boxShadow: `inset 2px 0 0 ${colors.navActive}`,
    },
  }),
};
